import { Check, ArrowUpRight, Clock, Users } from "lucide-react";
import { Reveal, SectionTag, SectionHeading } from "./Reveal";
import { scrollToId } from "../lib/scroll";
import { CONTACT } from "../lib/site";

const spotlight = (e) => {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty("--x", `${e.clientX - r.left}px`);
  el.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const PLANS = [
  {
    name: "Order Management",
    price: 999,
    blurb: "A dedicated order specialist, live on your desk.",
    tiers: [
      "3–5 headcount — $999 per employee / month",
      "1–2 headcount — $1,100 per employee / month",
      "6+ headcount — custom quote",
    ],
    features: [
      "Live order entry & real-time processing",
      "Vendor follow-ups, RAs & backorders",
      "Proof dispatch & approval chasing",
      "Tracking updates & daily reporting",
      "Dedicated specialist working live in your working hours, virtually",
    ],
    featured: false,
    testId: "pricing-card-999-order-management",
  },
  {
    name: "Graphic Designing",
    price: 1399,
    blurb: "A dedicated designer for decoration-ready art.",
    tiers: [
      "1–2 designers — $1,399 per designer / month",
      "3+ designers — custom quote",
    ],
    features: [
      "Vector artwork conversion & redraws",
      "1 dedicated designer · 8 working hours per day",
      "Virtual samples & product mockups",
      "Flyers, catalogs & brand collateral",
      "Same-day turnaround on rush jobs",
    ],
    featured: true,
    testId: "pricing-card-1399-graphic-design",
  },
];

const Pricing = () => (
  <section id="pricing" className="relative bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionTag index="05" label="Pricing" testId="pricing-section-tag" />
      <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div className="max-w-2xl">
          <SectionHeading>Dedicated staff, one flat monthly rate</SectionHeading>
        </div>
        <Reveal delay={0.15}>
          <p
            data-testid="pricing-hours-alignment-text"
            className="flex max-w-sm items-start gap-2.5 text-sm leading-relaxed text-slate-500"
          >
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#2E3192]" />
            {CONTACT.hoursLine} Per-employee pricing, scaled to your team size.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {PLANS.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.12}>
            <div
              data-testid={p.testId}
              onMouseMove={spotlight}
              className={`spotlight-card relative h-full rounded-2xl border p-8 transition-transform duration-500 hover:-translate-y-1.5 sm:p-10 ${
                p.featured
                  ? "border-[#2E3192]/30 bg-gradient-to-b from-[#2E3192]/[0.06] to-white"
                  : "border-slate-200 bg-white"
              }`}
            >
              {p.featured && (
                <span className="absolute right-6 top-6 rounded-full bg-[#6DC8F0] px-3 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#07090E]">
                  Most Popular
                </span>
              )}
              <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#2E3192]">
                Per Employee / Month
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-slate-900 sm:text-3xl">{p.name}</h3>
              <p className="mt-2 text-sm text-slate-500">{p.blurb}</p>
              <div className="mt-7 flex items-end gap-2">
                <span className="text-xs uppercase tracking-widest text-slate-400">Starting from</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-extrabold text-slate-900 sm:text-6xl">
                  ${p.price.toLocaleString()}
                </span>
                <span className="text-sm text-slate-500">USD / month</span>
              </div>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  <Users className="h-3.5 w-3.5 text-[#2E3192]" />
                  Headcount pricing
                </p>
                <ul className="mt-2.5 space-y-1.5">
                  {p.tiers.map((t) => (
                    <li key={t} className="text-xs leading-relaxed text-slate-600 sm:text-sm">
                      {t}
                    </li>
                  ))}
                </ul>
              </div>

              <ul className="mt-8 space-y-3 border-t border-slate-200 pt-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2E3192]" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`${p.testId}-cta`}
                onClick={() => scrollToId("contact")}
                className={`group mt-9 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-colors duration-300 ${
                  p.featured
                    ? "bg-[#6DC8F0] text-[#07090E] hover:bg-[#2E3192] hover:text-white"
                    : "bg-[#2E3192] text-white hover:bg-[#6DC8F0] hover:text-[#07090E]"
                }`}
              >
                Request a Quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Pricing;
